import React, { useEffect, useState, useCallback, useRef } from "react";
import useFetchExecutionHistory from "../../hooks/useFetchExecutionHistory";
import { GridColDef, GridExpandMoreIcon } from "@mui/x-data-grid";
import { GRID_DETAIL_PANEL_TOGGLE_COL_DEF } from "@mui/x-data-grid-pro";
import {
  Grid,
  IconButton,
  Tooltip,
  CircularProgress,
  Paper,
} from "@mui/material";
import {
  downloadRulesHistoryFile,
  getFormatedDate,
  getFormattedDateTimeWithAMPM,
  getFormattedTime,
  normalizeJobStatus,
  useToast,
} from "../../services/utils";
import {
  downloadFileSecurely,
  getSecureValidationReportPath,
} from "../../services/fileDownloadService";
import EHistoryDataTable from "../../components/DataGrids/EHistoryDataGrid";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Box } from "@mui/material";
import dayjs from "dayjs";
import { enableRowGroupByColumnProps } from "../../components/DataGridProps/DataGridProps";
import Loader from "../../components/Molecules/Loader/Loader";
import { useAuth } from "../../contexts/AuthContext";

import {
  IconDeleteBlueSvg,
  IconEyeBase,
  IconFilterSvg,
  IconReRunBlue,
  IconAbortBlue,
  IconViewErrorSvg,
  IconDownloadBlueSvg,
  IconTimeline,
} from "../../common/utils/icons";
import ExecutionModeIcon from "../../components/Molecules/ExecutionMode/ExecutionModeIcon";
// import useFetchIncidentByExecutionId from "../../hooks/useFetchIncidentByExecutionId";
import SearchFilterBar from "../../components/Molecules/FilterSearch/SearchFilterBar";
import ConsolidatedReportsFilter from "../../components/Molecules/FilterSearch/ConsolidatedReportsFilter";
import {
  abortExecution,
  deleteComparisonExecutionbyID,
  getRuleExecutionJobStatus,
  getRuleResultByRuleId,
} from "../../services/rulesService";
import ConfirmationDialog from "../../components/Dialogs/ConfirmationDialog";
import ErrorPopover from "../../components/Popovers/ErrorPopover";
import DashboardJobstatusIcon from "../../common/utils/DashboardJobstatusIcon";
import useSyncNotifications from "../../hooks/useSyncNotifications";
// import useWebSocket from "../../hooks/useWebSocket"; // No longer needed
import LimitChractersWithTooltip from "../../common/utils/LimitChractersWithTooltip";
import { getExecutionSubGroupName } from "../../utils/executionGroup";
import useExecutionSubGroupMap from "../../hooks/useExecutionSubGroupMap";
import { DynamicVerticalTimelineDialog } from "../../components/VerticalTimeline";
import { COMPARISON_HISTORY_TIMELINE_STEPS } from "../../constants/comparisonTimelineSteps.enum";
import { useRowsPerPage } from "../../hooks/useRowsPerPage";
import { useThemeVersion } from "../../contexts/ThemeContext";
import ScoreBadge from "../../components/Scoring/ScoreBadge";
import ScoreBreakdownDialog from "../../components/Scoring/ScoreBreakdownDialog";
import { getComparisonScore } from "../../services/scoringService";
import { IComparisonScore } from "../../types/Scoring.type";

/**
 * Inline cell component that displays a Comparison Execution Score badge using score data
 * from the history API response (no separate API call per row).
 * Full score details are fetched on-demand only when the breakdown dialog is opened.
 */
const ExecutionScoreCell: React.FC<{
  executionId: number;
  jobStatus: string;
  score: number | null | undefined;
  scoreCalculatedOn: string | null | undefined;
}> = ({ executionId, jobStatus, score, scoreCalculatedOn }) => {
  const [fullScoreData, setFullScoreData] =
    React.useState<IComparisonScore | null>(null);
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [dialogLoading, setDialogLoading] = React.useState(false);

  const normalizedStatus = jobStatus?.toLowerCase();
  const isCompleted =
    normalizedStatus === "completed" ||
    normalizedStatus === "completed_with_errors";

  if (!isCompleted || score == null) {
    return <ScoreBadge score={null} size="small" />;
  }

  const handleBadgeClick = async () => {
    setDialogOpen(true);
    if (!fullScoreData && executionId) {
      setDialogLoading(true);
      try {
        const data = await getComparisonScore(executionId);
        setFullScoreData(data || null);
      } catch {
        /* ignore */
      } finally {
        setDialogLoading(false);
      }
    }
  };

  return (
    <>
      <ScoreBadge
        score={score}
        size="small"
        showPrefix={false}
        onClick={handleBadgeClick}
        tooltipText="Click to view score breakdown"
      />
      <ScoreBreakdownDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        type="comparison"
        scoreData={fullScoreData}
        loading={dialogLoading}
      />
    </>
  );
};

interface RuleHistoryData {
  domainId: number | null;
  ruleId: number | null;
  fromDate: string | undefined;
}
interface RuleSearchData {
  search_by: string | null;
  search_query: string | null;
  search_date?: string;
}

// Centralized Filter Configuration for ExecutionHistory
const EXECUTION_FILTER_CONFIG = {
  // Default filter state - single source of truth
  DEFAULT_STATE: {
    start_date: null,
    end_date: null,
    domain_id: null,
    rule_id: null,
    run_id: null,
    execution_id: null,
    rule_name: "",
    run: "",
    status: "",
    classification: "",
    reason: "",
    assigned_user: "",
    assigned_by: "",
    execution_status: "",
  },

  // Fields to persist in URL - extracted from default state keys
  get URL_FIELDS() {
    return Object.keys(this.DEFAULT_STATE);
  },

  // Field type mappings for URL serialization/deserialization
  FIELD_TYPES: {
    ID_FIELDS: ["domain_id", "rule_id", "run_id", "execution_id"],
    STRING_FIELDS: [
      "rule_name",
      "run",
      "status",
      "classification",
      "reason",
      "assigned_user",
      "assigned_by",
      "execution_status",
    ],
    DATE_FIELDS: ["start_date", "end_date"],
  },
} as const;
const ExecutionHistory = () => {
  const defaultPage = process.env.REACT_APP_DEFAULT_PAGE;
  const { rowsPerPage } = useRowsPerPage();
  // DQ-8528: determine which UI generation is active
  const { uiVersion } = useThemeVersion();
  const useNewTheme = uiVersion === "new";
  const queryParams = new URLSearchParams(window.location.search);

  const navigate = useNavigate();
  const { showToast } = useToast();
  const { hasPermission } = useAuth();
  const executionSubGroupMap = useExecutionSubGroupMap();

  // URL search params for filter persistence
  const [searchParams, setSearchParams] = useSearchParams();

  // Ref to track if we're updating from URL changes to prevent circular updates
  const isUpdatingFromURL = useRef(false);

  // Permission checks for rule execution history actions
  const canViewRuleResult = hasPermission("rule_result", "view");
  const canDeleteRuleResult = hasPermission("rule_result", "delete");
  const canExecuteRule = hasPermission("rule", "execute");
  const canViewTimeline = hasPermission("execution_checkpoints", "view");
  const [isloading, setIsLoading] = useState<boolean>(false);
  const [isBackdropLoading, setIsBackdropLoading] = useState<boolean>(false);
  const [isDeleteLoading, setIsDeleteLoading] = useState<boolean>(false);
  const [fileData, setFileData] = useState<any>({
    items: [],
    total: 0,
    page: 0,
    size: 0,
    pages: 0,
  });
  const [expandedRow, setExpandedRow] = useState<any>([]);
  // Timeline dialog state
  const [timelineDialogOpen, setTimelineDialogOpen] = useState(false);
  // Execution ID for timeline (updated when opening dialog)
  const [currentExecutionId, setCurrentExecutionId] = useState<number | null>(
    null,
  );
  const [formData, setFormData] = useState({
    run_name: "",
    run_date: dayjs().subtract(1, "day"),
    report_type: "",
  });
  const [page, setPage] = useState<number>(
    defaultPage ? parseInt(defaultPage) : 1,
  );
  const [pSize, setPSize] = useState<number>(rowsPerPage);

  // Helper function to get initial filter state from URL
  const getInitialFilterState = () => {
    // Use centralized default state
    const defaultFilterState = { ...EXECUTION_FILTER_CONFIG.DEFAULT_STATE };

    // Extract filter parameters from URL
    const urlFilters: any = {};

    // Handle ID fields (convert to number if present)
    EXECUTION_FILTER_CONFIG.FIELD_TYPES.ID_FIELDS.forEach((field) => {
      const value = searchParams.get(field);
      if (value && !isNaN(Number(value))) {
        urlFilters[field] = Number(value);
      }
    });

    // Handle string fields
    EXECUTION_FILTER_CONFIG.FIELD_TYPES.STRING_FIELDS.forEach((field) => {
      const value = searchParams.get(field);
      if (value) {
        urlFilters[field] = value;
      }
    });

    // Handle date fields
    EXECUTION_FILTER_CONFIG.FIELD_TYPES.DATE_FIELDS.forEach((field) => {
      const value = searchParams.get(field);
      if (value) {
        // Validate date format (YYYY-MM-DD)
        const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
        if (dateRegex.test(value)) {
          urlFilters[field] = value;
        }
      }
    });

    return { ...defaultFilterState, ...urlFilters };
  };

  // Helper function to serialize filter data to URL parameters
  const serializeFiltersToURL = (filters: any) => {
    const newParams = new URLSearchParams(searchParams);

    // Use centralized field configuration
    const fieldsToSerialize = EXECUTION_FILTER_CONFIG.URL_FIELDS;

    // Remove all existing filter parameters first
    fieldsToSerialize.forEach((field) => {
      newParams.delete(field);
    });

    // Add non-empty filter values to URL
    fieldsToSerialize.forEach((field) => {
      const value = filters[field];
      if (value !== null && value !== undefined && value !== "") {
        if (typeof value === "string") {
          newParams.set(field, value);
        } else {
          newParams.set(field, String(value));
        }
      }
    });

    return newParams;
  };

  // Helper function to clear filters (state only, URL will be updated by useEffect)
  const clearFilters = useCallback(() => {
    // Reset filter state to default - URL will be updated automatically by the filter useEffect
    setSearchFilterData({ ...EXECUTION_FILTER_CONFIG.DEFAULT_STATE });
  }, []);

  const [searchFilterData, setSearchFilterData] = useState<any>(() =>
    getInitialFilterState(),
  );
  const [isConsolidatedFilterVisible, setIsConsolidatedFilterVisible] =
    useState(false);

  // const [incidentData, setIncidentData] = useState<any>([]);
  // const [incidentResultIds, setIncidentResultIds] = useState();

  const [
    openComparisonExecutionConfirmation,
    setOpenComparisonExecutionConfirmation,
  ] = useState(false);

  const [openAbortExecutionConfirmation, setOpenAbortExecutionConfirmation] =
    useState(false);

  const [deleteExecutionId, setDeleteExecutionId] = useState<number | null>(
    null,
  );

  const [abortExecutionId, setAbortExecutionId] = useState<number | null>(null);

  // Error popover state
  const [errorAnchorEl, setErrorAnchorEl] = useState<null | HTMLElement>(null);
  const [errorData, setErrorData] = useState<any>(null);
  const [isErrorLoading, setIsErrorLoading] = useState(false);
  const [isFileDataCompleted, setIsFileDataCompleted] = useState(false);

  const [ruleHistoryParams, setRuleHistoryParams] = useState<RuleHistoryData>({
    domainId: Number(queryParams.get("domainId")),
    ruleId: Number(queryParams.get("ruleId")),
    fromDate: queryParams.get("fromDate")?.toString(),
  });

  // Download state management for output logs
  const [downloadingFiles, setDownloadingFiles] = useState<Set<string>>(
    new Set(),
  );
  const [downloadProgress, setDownloadProgress] = useState<
    Map<string, { percentage: number; loaded: number; total: number }>
  >(new Map());

  // Download functionality for output logs
  const handleSecureDownload = async (filepath: string, fileName: string) => {
    if (downloadingFiles.has(filepath)) return; // Prevent duplicate downloads

    setDownloadingFiles((prev) => new Set(prev).add(filepath));
    setDownloadProgress((prev) =>
      new Map(prev).set(filepath, { percentage: 0, loaded: 0, total: 0 }),
    );

    try {
      await downloadFileSecurely(
        filepath,
        fileName,
        true, // isExtractAndEncodeFilePath
        (progress) => {
          // Update progress for this specific file
          setDownloadProgress((prev) => new Map(prev).set(filepath, progress));
        },
      );
    } catch (error) {
      console.error(`❌ Enhanced download failed for ${fileName}:`, error);
      // Error handling is done in the downloadFileSecurely function
    } finally {
      setDownloadingFiles((prev) => {
        const newSet = new Set(prev);
        newSet.delete(filepath);
        return newSet;
      });
      setDownloadProgress((prev) => {
        const newMap = new Map(prev);
        newMap.delete(filepath);
        return newMap;
      });
    }
  };

  const getSecureReportPath = (path: string) => {
    if (path) {
      const { fileName } = getSecureValidationReportPath(path);
      return {
        downloadAction: () => handleSecureDownload(path, fileName),
        fileName,
      };
    }
    return null;
  };

  // Update URL when filter state changes (only when not updating from URL)
  useEffect(() => {
    // Skip URL update if we're currently updating from URL changes
    if (isUpdatingFromURL.current) {
      return;
    }

    // Serialize current filter state to URL parameters
    const newParams = serializeFiltersToURL(searchFilterData);

    // Only update URL if the serialized params differ from current URL
    const currentParamsString = searchParams.toString();
    const newParamsString = newParams.toString();

    if (currentParamsString !== newParamsString) {
      setSearchParams(newParams);
    }
  }, [searchFilterData, searchParams, setSearchParams]);

  // Update filter state when URL changes (for back/forward navigation)
  useEffect(() => {
    // Skip if we're currently updating from URL changes to prevent circular updates
    if (isUpdatingFromURL.current) {
      return;
    }

    // Extract current filter values from URL
    const urlFilterState = getInitialFilterState();

    // Check if URL filter state differs from current filter state
    const hasFilterChanges = Object.keys(urlFilterState).some((key) => {
      const urlValue = urlFilterState[key];
      const currentValue = searchFilterData[key];

      // Handle null/undefined/empty string comparisons
      const normalizedUrlValue =
        urlValue === null || urlValue === undefined || urlValue === ""
          ? null
          : urlValue;
      const normalizedCurrentValue =
        currentValue === null ||
        currentValue === undefined ||
        currentValue === ""
          ? null
          : currentValue;

      return normalizedUrlValue !== normalizedCurrentValue;
    });

    // Update filter state if URL has different values
    if (hasFilterChanges) {
      // Set flag to prevent URL updates while we update state
      isUpdatingFromURL.current = true;

      setSearchFilterData(urlFilterState);

      // Reset flag after state update is processed
      setTimeout(() => {
        isUpdatingFromURL.current = false;
      }, 100);
    }
  }, [searchParams]); // Only depend on searchParams to detect URL changes

  // Memoize searchFilterData updates to prevent unnecessary re-renders
  const updateSearchFilter = useCallback((newData: any) => {
    setSearchFilterData(newData);
  }, []);
  const [executionList] = useFetchExecutionHistory({
    setIsLoading: setIsBackdropLoading,
    page,
    pSize,
    domainId: ruleHistoryParams.domainId,
    ruleId: ruleHistoryParams.ruleId,
    fromDate: ruleHistoryParams.fromDate,
    searchFilterData,
  });

  // Get handleStorageChange from useSyncNotifications
  // This will automatically sync with notifications from the context
  // useSyncNotifications(setFileData, fileData);

  // handleRowExpand: fetch full detail data when a row is expanded
  const handleRowExpand = useCallback(async (rowId: number) => {
    try {
      const detail = await getRuleResultByRuleId({
        currentRuleResultId: rowId,
      });
      return detail;
    } catch (error) {
      console.error("Error fetching rule result detail:", error);
      return null;
    }
  }, []);

  // const [fetchIncidentData] = useFetchIncidentByExecutionId({
  //   setIsLoading,
  //   data: incidentResultIds,
  // });
  // useEffect(() => {
  //   setIncidentData(fetchIncidentData);
  // }, [fetchIncidentData, page]);

  useEffect(() => {
    setIsFileDataCompleted(false);
    if (executionList?.items?.length > 0) {
      const normalizedData = {
        ...executionList,
        items: executionList.items.map(normalizeJobStatus),
      };
      setFileData(normalizedData);
    } else {
      setFileData({ items: [], total: 0, page: 1, size: 25 });
    }
  }, [executionList]);

  useEffect(() => {
    // Only set completed to true when fileData has been properly updated
    // Check if fileData matches what we expect from executionList
    const expectedData = executionList || {
      items: [],
      total: 0,
      page: 1,
      size: 25,
    };

    if (
      fileData &&
      fileData.items?.length === expectedData.items?.length &&
      fileData.total === expectedData.total
    ) {
      setIsFileDataCompleted(true);
    }
  }, [fileData, executionList]);

  // useEffect(() => {
  //   const updatedFileData = handleRuleExecutionList(executionList);
  //   // Update fileData state
  //   if (updatedFileData?.items?.length > 0) {
  //     const newData = updatedFileData.items.map((file: any) => {
  //       const fileItem: any =
  //         incidentData &&
  //         incidentData.length > 0 &&
  //         incidentData.find((incidentItem: any) => {
  //           return Number(incidentItem?.ExecutionId) === Number(file.id);
  //         });
  //       if (fileItem) {
  //         return {
  //           ...file,
  //           IncidentStatus: fileItem?.IncidentStatus,
  //         };
  //       }
  //       return file;
  //     });
  //     setFileData({ ...updatedFileData, items: newData });
  //   }
  // }, [incidentData, page]);

  useEffect(() => {
    const handleStorageChange = () => {
      const storedNotifications = localStorage.getItem("notifications");

      if (storedNotifications && fileData?.items?.length > 0) {
        try {
          const notifications = JSON.parse(storedNotifications);

          setFileData((prevFileData: any) => {
            const updatedItems = prevFileData.items
              .map((item: any) => {
                const matchingNotification = notifications.find(
                  (notification: any) =>
                    (notification?.execution_type === "rule" ||
                      notification?.execution_type === "rule_rerun") &&
                    notification?.execution_id === item?.id,
                );

                // if (matchingNotification?.execution_status === "failed") {
                //   return null; // Remove this item
                // }

                if (matchingNotification?.job_status) {
                  return {
                    ...item,
                    job_status: matchingNotification.job_status.toLowerCase(),
                  };
                }

                return item;
              })
              .filter(Boolean); // Remove nulls (failed items)

            const hasChanges =
              updatedItems.length !== prevFileData.items.length ||
              updatedItems.some(
                (newItem: any, index: number) =>
                  newItem.job_status !== prevFileData.items[index]?.job_status,
              );

            return hasChanges
              ? { ...prevFileData, items: updatedItems }
              : prevFileData;
          });
        } catch (error) {
          console.error(
            "Error parsing notifications from localStorage:",
            error,
          );
        }
      }
    };

    window.addEventListener("storage", handleStorageChange);
    const checkInterval = setInterval(handleStorageChange, 1000);
    handleStorageChange();

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      clearInterval(checkInterval);
    };
  }, [isFileDataCompleted]);

  /**
   * Set table coloums for execution history
   */
  const columns: GridColDef[] = [
    {
      ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
      renderCell: (params: any) => (
        <IconButton size="small" tabIndex={-1} className="groupby-expand-icon">
          <GridExpandMoreIcon
            sx={{
              transform: `rotateZ(${
                expandedRow.includes(params.row.id) ? 180 : 0
              }deg)`,
              transition: (theme) =>
                theme.transitions.create("transform", {
                  duration: theme.transitions.duration.shortest,
                }),
            }}
            fontSize="inherit"
          />
        </IconButton>
      ),
    },
    {
      field: "id",
      headerName: "Execution ID",
      flex: 1,
      minWidth: 100,
      groupable: false,
      renderCell: (params: any) => {
        return (
          <Tooltip
            title={`Execution Group Id: ${params?.row?.run_id}`}
            placement="top"
            arrow
          >
            <span style={{ cursor: "pointer" }}>{params.value}</span>
          </Tooltip>
        );
      },
    },
    {
      field: "rule_id",
      headerName: "Rule ID",
      flex: 1,
      minWidth: 100,
      groupable: false,
      renderCell: (params: any) => {
        const handleRuleIdClick = () => {
          const ruleId = params?.row?.rule_id;
          const domainId = params?.row?.domain_id;
          if (ruleId && domainId) {
            window.open(`/rules/${domainId}/view/${ruleId}`, "_blank");
          }
        };

        return (
          <Tooltip
            title="Click to view rule details"
            placement="top"
            arrow
            componentsProps={{
              tooltip: {
                sx: {
                  pointerEvents: "none",
                },
              },
            }}
          >
            <span
              onClick={handleRuleIdClick}
              style={{
                cursor: "pointer",
                textDecoration: "none",
                display: "block",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.textDecoration = "underline";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.textDecoration = "none";
              }}
            >
              <Box
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {params?.value}
              </Box>
            </span>
          </Tooltip>
        );
      },
    },
    {
      field: "execution_name",
      headerName: "Execution Name",
      flex: 1,
      minWidth: 160,
      groupable: false,
      // renderCell: (params: any) => {
      //   return <LimitChractersWithTooltip value={params?.value} />;
      // },
    },
    {
      field: "name",
      headerName: "Rule Name",
      flex: 1,
      minWidth: 210,
      groupable: false,
      renderCell: (params: any) => {
        const handleRuleNameClick = () => {
          const ruleId = params?.row?.rule_id;
          const domainId = params?.row?.domain_id;
          if (ruleId && domainId) {
            window.open(`/rules/${domainId}/view/${ruleId}`, "_blank");
          }
        };

        return (
          <Tooltip
            title="Click to view rule details"
            placement="top"
            arrow
            componentsProps={{
              tooltip: {
                sx: {
                  pointerEvents: "none",
                },
              },
            }}
          >
            <span
              onClick={handleRuleNameClick}
              style={{
                cursor: "pointer",
                textDecoration: "none",
                display: "block",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.textDecoration = "underline";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.textDecoration = "none";
              }}
            >
              <Box
              // className="limit-chracters"
              // sx={{
              //   overflow: "hidden",
              //   textOverflow: "ellipsis",
              //   whiteSpace: "nowrap",
              //   maxWidth: "95%",
              // }}
              >
                {params?.value}
              </Box>
            </span>
          </Tooltip>
        );
      },
    },
    {
      field: "run_name",
      headerName: "Execution Group Name",
      flex: 1,
      minWidth: 100,
      groupable: false,
      renderCell: (params: any) => {
        return <LimitChractersWithTooltip value={params?.value} />;
      },
    },
    {
      field: "execution_sub_group_id",
      headerName: "Execution Sub Group ID",
      minWidth: 90,
      groupable: false,
      renderCell: (params: any) => {
        return <span>{params?.value}</span>;
      },
    },
    {
      field: "execution_sub_group_code",
      headerName: "Execution Sub Group Name",
      flex: 1,
      minWidth: 100,
      groupable: false,
      renderCell: (params: any) => {
        return (
          <LimitChractersWithTooltip
            value={getExecutionSubGroupName(params?.row, executionSubGroupMap)}
          />
        );
      },
    },
    {
      field: "domain_name",
      headerName: "Domain Name",
      flex: 1,
      minWidth: 100,
      renderCell: (params: any) => {
        return <LimitChractersWithTooltip value={params?.value} />;
      },
    },
    {
      field: "execution_time",
      headerName: "Execution Date & Time",
      minWidth: 180,
      groupable: false,
      renderCell: (params) => {
        if (params.rowNode.type === "group") {
          return null;
        }
        const executionTime = params?.value?.execution_time || params.value;
        const formattedTime = getFormattedDateTimeWithAMPM(executionTime);
        return (
          <div>
            <div>{formattedTime}</div>
          </div>
        );
      },
    },

    {
      field: "job_status",
      headerName: "Execution Status",
      flex: 1,
      minWidth: 120,
      headerAlign: "center",
      align: "center",
      groupable: false,
      renderCell: (params: any) => (
        <DashboardJobstatusIcon status={params?.row?.job_status} />
      ),
    },
    {
      field: "execution_mode",
      headerName: "Execution Mode",
      flex: 1,
      minWidth: 120,
      headerAlign: "center",
      align: "center",
      groupable: false,
      renderCell: (params: any) => (
        <ExecutionModeIcon executionMode={params?.row?.execution_mode} />
      ),
    },
    {
      field: "execution_score",
      headerName: "Success Rate",
      flex: 0.7,
      minWidth: 80,
      headerAlign: "center",
      align: "center",
      groupable: false,
      sortable: false,
      filterable: false,
      renderCell: (params: any) => (
        <ExecutionScoreCell
          executionId={params?.row?.id}
          jobStatus={params?.row?.job_status}
          score={params?.row?.score}
          scoreCalculatedOn={params?.row?.score_calculated_on}
        />
      ),
    },
    {
      field: "action",
      headerName: "Actions",
      align: "left",
      headerAlign: "left",
      width: 140,
      groupable: false,
      renderCell: (params) => {
        if (params.rowNode.type === "group") {
          return null;
        }
        const handleViewClick = (incidentTab?: any) => {
          const basePath = `/rules-execution-history/${params?.row.rule_id}/${params?.row.id}/dashboard`;

          const navigatePath = incidentTab
            ? `${basePath}#incidentTab`
            : basePath;

          navigate(navigatePath, {
            state: {
              rowData: params?.row,
            },
          });
        };
        const handleAbortExecution = () => {
          setAbortExecutionId(params?.row?.id);
          setOpenAbortExecutionConfirmation(true);
        };

        const handleViewError = async (
          event: React.MouseEvent<HTMLElement>,
        ) => {
          // Persist the event target before async operations
          const target = event.currentTarget;

          setErrorAnchorEl(target);
          setIsErrorLoading(true);

          try {
            const response = await getRuleExecutionJobStatus(params?.row?.id);
            setErrorData(response);
          } catch (error) {
            console.error("Error fetching rule execution job status:", error);
          } finally {
            setIsErrorLoading(false);
          }
        };
        return (
          <>
            {(params?.row?.job_status === "completed" ||
              params?.row?.job_status === "completed_with_errors") &&
              canViewRuleResult && (
                <Tooltip title="View Dashboard" placement="top" arrow>
                  <IconButton
                    className="datagrid-action-btn"
                    color="inherit"
                    onClick={() => handleViewClick()}
                  >
                    <IconEyeBase />
                  </IconButton>
                </Tooltip>
              )}

            {(params?.row?.job_status === "in_progress" ||
              params?.row?.job_status === "in_queue") &&
              (params?.row?.execution_mode === "long_running_job_worker" ||
                params?.row?.execution_mode === "normal_job_worker") &&
              canDeleteRuleResult && (
                <Tooltip title="Abort Execution" placement="top" arrow>
                  <IconButton
                    className="datagrid-action-btn"
                    color="inherit"
                    onClick={() => handleAbortExecution()}
                  >
                    <IconAbortBlue />
                  </IconButton>
                </Tooltip>
              )}
            {(params?.row?.job_status === "failed" ||
              params?.row?.job_status === "aborted") &&
              canViewRuleResult && (
                <Tooltip title="View Error" placement="top" arrow>
                  <IconButton
                    className="datagrid-action-btn"
                    color="inherit"
                    onClick={handleViewError}
                  >
                    <IconViewErrorSvg />
                  </IconButton>
                </Tooltip>
              )}
            {
              // params?.row?.IncidentStatus && (
              //   <Tooltip title="View Incidents" placement="top" arrow>
              //     <IconButton
              //       className="datagrid-action-btn"
              //       color="inherit"
              //       onClick={() => handleViewClick("incidentTab")}
              //     >
              //       {params?.row?.IncidentStatus === "Triggered" ? (
              //         <IconIncidentTriggeredSvg />
              //       ) : params?.row?.IncidentStatus === "Acknowledged" ? (
              //         <IconIncidentAcknowledgeSvg />
              //       ) : params?.row?.IncidentStatus === "Resolve" ? (
              //         <IconIncidentResolveSvg />
              //       ) : null}
              //     </IconButton>
              //   </Tooltip>
              // )
            }
            {(params?.row?.job_status === "completed" ||
              params?.row?.job_status === "completed_with_errors") &&
              canExecuteRule && (
                <Tooltip title="Re-Run" placement="top" arrow>
                  <IconButton
                    className="datagrid-action-btn"
                    color="inherit"
                    onClick={() =>
                      navigate(
                        `/rules/${params?.row.domain_id}/re-run-rule-execution/${params?.row.rule_id}?re-run=true&execution-id=${params?.row.id}&run-id=${params?.row?.run_id}&run-name=${params?.row?.run_name}&execution-sub-group-id=${params?.row?.execution_sub_group_id}&execution-sub-group-code=${params?.row?.execution_sub_group_code}`,
                      )
                    }
                  >
                    <IconReRunBlue />
                  </IconButton>
                </Tooltip>
              )}

            {(params?.row?.job_status === "completed" ||
              params?.row?.job_status === "completed_with_errors" ||
              params?.row?.job_status === "failed" ||
              params?.row?.job_status === "aborted") &&
              canDeleteRuleResult && (
                <Tooltip title="Delete" placement="top" arrow>
                  <IconButton
                    className="datagrid-action-btn"
                    color="inherit"
                    onClick={() => {
                      setDeleteExecutionId(params.row.id);
                      setOpenComparisonExecutionConfirmation(true);
                    }}
                  >
                    <IconDeleteBlueSvg />
                  </IconButton>
                </Tooltip>
              )}
            {/* Conditionally render download button only when output logs are available */}
            {(params?.row?.output_logs ||
              params?.row?.additional_properties?.supporting_documents
                ?.output_logs) &&
              (() => {
                const outputLogsPath =
                  params?.row?.output_logs ||
                  params?.row?.additional_properties?.supporting_documents
                    ?.output_logs;
                const isDownloading = downloadingFiles.has(outputLogsPath);
                const reportPath = getSecureReportPath(outputLogsPath);

                return (
                  <Tooltip
                    title={
                      isDownloading ? "Downloading..." : "Download Output Logs"
                    }
                    placement="top"
                    arrow
                  >
                    <Box sx={{ position: "relative", display: "inline-flex" }}>
                      <IconButton
                        className="datagrid-action-btn"
                        color="inherit"
                        disabled={isDownloading}
                        onClick={() => {
                          if (!isDownloading && reportPath) {
                            reportPath.downloadAction();
                          }
                        }}
                        sx={{
                          "& svg": {
                            width: "20px",
                            height: "20px",
                            fill: isDownloading
                              ? "var(--auto-gray)"
                              : "var(--auto-green)",
                          },
                          cursor: isDownloading ? "not-allowed" : "pointer",
                        }}
                      >
                        {isDownloading ? (
                          <CircularProgress
                            size={16}
                            sx={{ color: "var(--auto-green)" }}
                          />
                        ) : (
                          <IconDownloadBlueSvg />
                        )}
                      </IconButton>
                      {/* Progress percentage display */}
                      {isDownloading &&
                        downloadProgress.has(outputLogsPath) && (
                          <Box
                            sx={{
                              position: "absolute",
                              top: -8,
                              right: -8,
                              backgroundColor: "var(--auto-green)",
                              color: "white",
                              borderRadius: "50%",
                              width: 20,
                              height: 20,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: "10px",
                              fontWeight: "bold",
                            }}
                          >
                            {downloadProgress.get(outputLogsPath)?.percentage ||
                              0}
                            %
                          </Box>
                        )}
                    </Box>
                  </Tooltip>
                );
              })()}
            {params?.row?.job_status !== "completed" &&
              params?.row?.job_status !== "completed_with_errors" &&
              canViewTimeline && (
                <Tooltip title="View Timeline" placement="top" arrow>
                  <IconButton
                    className="datagrid-action-btn"
                    color="inherit"
                    onClick={() => {
                      setCurrentExecutionId(params?.row?.id);
                      setTimelineDialogOpen(true);
                    }}
                  >
                    <IconTimeline />
                  </IconButton>
                </Tooltip>
              )}
          </>
        );
      },
    },
  ];

  const handleSubmitReport = async (event: any) => {
    // event.preventDefault();
    navigate(
      `/rules-execution-history/consolidate-results?runName=${formData.run_name}&reportType=${formData.report_type}&runDate=${formData?.run_date}`,
    );
  };

  const handleDelete = async () => {
    if (deleteExecutionId) {
      try {
        setIsDeleteLoading(true);
        const result = await deleteComparisonExecutionbyID(deleteExecutionId);
        if (result) {
          setFileData((prev: any) => ({
            ...prev,
            items: prev?.items?.filter(
              (prevItem: any) => prevItem.id !== deleteExecutionId,
            ),
          }));
          showToast(`${deleteExecutionId} deleted successfully`, "success");
        }
      } catch (error: any) {
        console.error("Error deleting execution:", error);
        showToast(error?.message || "Failed to delete execution", "error");
      } finally {
        setIsDeleteLoading(false);
      }
    }
  };

  const handleComparisonExecutionDelete = () => {
    setOpenComparisonExecutionConfirmation(false);
    handleDelete();
  };

  const handleAbortExecution = async () => {
    if (abortExecutionId) {
      try {
        setIsDeleteLoading(true);
        const result = await abortExecution(abortExecutionId);
        if (result?.status === 200) {
          setFileData((prev: any) => ({
            ...prev,
            items:
              prev?.items?.map((item: any) =>
                item.id === abortExecutionId
                  ? { ...item, job_status: "aborted" }
                  : item,
              ) || [],
          }));
          showToast(`${abortExecutionId} aborted successfully`, "success");
        }
      } catch (error) {
        console.error("Error aborting execution:", error);
      } finally {
        setIsDeleteLoading(false);
      }
    }
  };

  const handleAbortExecutionDelete = () => {
    setOpenAbortExecutionConfirmation(false);
    handleAbortExecution();
  };
  return (
    <>
      {(isBackdropLoading || isDeleteLoading) && <Loader isLoading={true} />}
      {/* DQ-8528: new theme → MUI Paper container; legacy → existing SCSS Box (AC-1, AC-2) */}
      {useNewTheme ? (
        <Paper
          sx={{
            p: 2,
            borderRadius: 2,
            bgcolor: "background.paper",
            boxShadow: 1,
            mb: 1,
          }}
        >
          <Grid container rowSpacing={2.5} columnSpacing={2}>
            <Grid item xs>
              <SearchFilterBar
                largeWidth={true}
                setSearchFilterData={updateSearchFilter}
                searchFilterData={searchFilterData}
                requiredFields={[
                  "start_date",
                  "domain_list",
                  "rule_list",
                  "run_list",
                  "execution_sub_group_list",
                  "execution_id",
                ]}
                primaryFields={["domain_list", "rule_list"]}
                fieldRequirements={{
                  dateTitle: "From Date",
                  tooltipRequired: true,
                  includeDomainId: true,
                  includeRuleId: true,
                  includeRunId: true,
                }}
                useNewTheme={true}
              />
            </Grid>
            <Grid item>
              <button
                className="btn-orange btn-border cursor-pointer"
                onClick={() => setIsConsolidatedFilterVisible(true)}
                color="secondary"
              >
                Consolidated Reports
              </button>
              <ConsolidatedReportsFilter
                setFormData={setFormData}
                isFilterVisible={isConsolidatedFilterVisible}
                setIsFilterVisible={setIsConsolidatedFilterVisible}
                formData={formData}
                handleSubmitReport={handleSubmitReport}
              />
            </Grid>
          </Grid>
        </Paper>
      ) : (
        <Box className="text-box-card list-page-card compact-text-box-card">
          <Grid container rowSpacing={2.5} columnSpacing={2}>
            <Grid item xs>
              <SearchFilterBar
                largeWidth={true}
                setSearchFilterData={updateSearchFilter}
                searchFilterData={searchFilterData}
                requiredFields={[
                  "start_date",
                  "domain_list",
                  "rule_list",
                  "run_list",
                  "execution_sub_group_list",
                  "execution_id",
                ]}
                primaryFields={["domain_list", "rule_list"]}
                fieldRequirements={{
                  dateTitle: "From Date",
                  tooltipRequired: true,
                  includeDomainId: true,
                  includeRuleId: true,
                  includeRunId: true,
                }}
              />
            </Grid>
            <Grid item>
              <button
                className="btn-orange btn-border cursor-pointer"
                onClick={() => setIsConsolidatedFilterVisible(true)}
                color="secondary"
              >
                Consolidated Reports
              </button>
              <ConsolidatedReportsFilter
                setFormData={setFormData}
                isFilterVisible={isConsolidatedFilterVisible}
                setIsFilterVisible={setIsConsolidatedFilterVisible}
                formData={formData}
                handleSubmitReport={handleSubmitReport}
              />
            </Grid>
          </Grid>
        </Box>
      )}

      <div className="execution-history-btn-group">
        <Box className="transaction-btn-group"></Box>
      </div>
      <EHistoryDataTable
        dataColumns={columns || []}
        dataRows={fileData?.items || []}
        loading={isloading}
        setLoading={setIsLoading}
        dataListTitle={"Comparison History"}
        isExportButtonRequired={true}
        className="dataTable no-radius hide-progress-icon"
        rowCount={fileData?.total || 0}
        paginationModel={{
          page: page - 1,
          pageSize: pSize,
        }}
        onPaginationModelChange={(params: any) => {
          if (params.pageSize !== pSize || params.page !== page - 1) {
            setPage(params.page + 1);
            setPSize(params.pageSize);
          }
        }}
        historyType={"Execution"}
        downloadFromAPIFile={() =>
          downloadRulesHistoryFile({
            setLoading: setIsBackdropLoading,
            searchFilterData,
          })
        }
        disableColumnFilter={false}
        rowGroupByColumnProps={enableRowGroupByColumnProps}
        fitViewport
        singlePageMaxHeightDiff={315}
        isShowInComparisonHistory={true}
        onRowExpand={handleRowExpand}
        useNewTheme={useNewTheme}
      />
      <ConfirmationDialog
        title={"Confirm removing Execution"}
        dialogContent={
          "Are you sure you want to delete this comparison execution data?"
        }
        handleCancel={() => setOpenComparisonExecutionConfirmation(false)}
        openConfirmation={openComparisonExecutionConfirmation}
        handleConfirm={handleComparisonExecutionDelete}
        isShowClassMb0={false}
      />
      <ConfirmationDialog
        title={"Confirm aborting Execution"}
        dialogContent={
          "Are you sure you want to abort this comparison execution data?"
        }
        handleCancel={() => setOpenAbortExecutionConfirmation(false)}
        openConfirmation={openAbortExecutionConfirmation}
        handleConfirm={handleAbortExecutionDelete}
        isShowClassMb0={false}
      />
      <ErrorPopover
        open={Boolean(errorAnchorEl)}
        anchorEl={errorAnchorEl}
        onClose={() => {
          setErrorAnchorEl(null);
          setErrorData(null); // Clear error data when closing
        }}
        errorData={errorData}
        isLoading={isErrorLoading}
        title="Rule Execution Error Details"
      />
      {/* Dynamic Vertical Timeline Dialog */}
      <DynamicVerticalTimelineDialog
        open={timelineDialogOpen}
        onClose={() => setTimelineDialogOpen(false)}
        executionId={currentExecutionId}
        jobType="comparison"
        title="Rule Execution Timeline"
        showActions={true}
        primaryButtonText="Close"
        timelineSteps={COMPARISON_HISTORY_TIMELINE_STEPS}
        onPrimaryAction={() => {
          setTimelineDialogOpen(false);
        }}
      />
    </>
  );
};

export default ExecutionHistory;
